/**
 * Select Adapter for shadcn
 * 
 * This component adapts the shadcn select to fit our design system.
 */
import React from 'react';

// Import the shadcn Select components
// Update these paths based on your actual shadcn implementation
import {
  Select as ShadcnSelect,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../select';

// Import utils for class merging
import { cn } from '@/lib/utils';

// Define the option type
export interface SelectOption {
  label: string;
  value: string;
  disabled?: boolean;
}

// Define the select interface based on our design system
export interface SelectProps {
  variant?: 'default' | 'filled' | 'outlined';
  size?: 'sm' | 'md' | 'lg';
  options: SelectOption[];
  value?: string;
  defaultValue?: string;
  placeholder?: string;
  error?: boolean | string;
  helperText?: string;
  leftIcon?: React.ReactNode;
  className?: string;
  onChange?: (value: string) => void;
  disabled?: boolean;
  [key: string]: any;
}

/**
 * Select adapter for shadcn
 *
 * This component adapts the shadcn select to fit our design system.
 * 
 * @example
 * ```tsx
 * <Select 
 *   options={[
 *     { label: 'Option 1', value: '1' },
 *     { label: 'Option 2', value: '2' },
 *   ]}
 *   placeholder="Select an option"
 * />
 * ```
 */
export const Select = React.forwardRef<HTMLButtonElement, SelectProps>(
  ({ 
    variant = 'default',
    size = 'md',
    options,
    value,
    defaultValue,
    placeholder = 'Select an option',
    error,
    helperText,
    leftIcon,
    className,
    onChange,
    disabled,
    ...props 
  }, ref) => {
    // Map size to class names
    const sizeClasses = {
      sm: 'h-8 text-sm',
      md: 'h-10 text-base',
      lg: 'h-12 text-lg'
    };
    
    // Map variant to class names
    const variantClasses = {
      default: 'border border-input bg-background',
      filled: 'border border-transparent bg-gray-100 hover:bg-gray-200',
      outlined: 'border border-gray-300 bg-transparent'
    };
    
    // Error state
    const errorClasses = error ? 'border-red-300 text-red-900 focus:ring-red-500' : '';
    
    // Find the selected option to display its label
    const selectedOption = options.find(option => option.value === value);
    
    return (
      <div className="w-full space-y-1">
        <ShadcnSelect
          value={value}
          defaultValue={defaultValue}
          onValueChange={onChange}
          disabled={disabled}
        >
          <SelectTrigger 
            ref={ref}
            className={cn(
              sizeClasses[size as keyof typeof sizeClasses],
              variantClasses[variant as keyof typeof variantClasses],
              errorClasses,
              leftIcon && 'pl-10',
              'focus:outline-none focus:ring-2 focus:ring-rose-500 dark:focus:ring-white focus:ring-offset-2',
              'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-rose-500 dark:focus-visible:ring-white focus-visible:ring-offset-2',
              className
            )}
            aria-invalid={!!error}
          >
            {leftIcon && (
              <div className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">
                {leftIcon}
              </div>
            )}
            <SelectValue placeholder={placeholder}>
              {selectedOption ? selectedOption.label : ''}
            </SelectValue>
          </SelectTrigger>
          <SelectContent>
            {options.map((option: SelectOption) => (
              <SelectItem 
                key={option.value} 
                value={option.value}
                disabled={option.disabled}
                className={cn(
                  'cursor-pointer',
                  // Remove orange; use primary red in both modes for highlight/checked
                  'data-[highlighted]:bg-rose-500 data-[highlighted]:text-white',
                  'dark:data-[highlighted]:bg-rose-500 dark:data-[highlighted]:text-white',
                  'data-[state=checked]:bg-rose-500 data-[state=checked]:text-white',
                  'dark:data-[state=checked]:bg-rose-500 dark:data-[state=checked]:text-white'
                )}
              >
                {option.label}
              </SelectItem>
            ))}
          </SelectContent>
        </ShadcnSelect>
        
        {(helperText || (typeof error === 'string' && error)) && (
          <p className={cn(
            "text-sm",
            error ? "text-red-500" : "text-gray-500"
          )}>
            {(typeof error === 'string' && error) || helperText}
          </p>
        )}
      </div>
    );
  }
);

Select.displayName = 'Select';

// Export subcomponents for more flexible usage
export { 
  ShadcnSelect as SelectRoot,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
};
